import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Heading } from "./NKDaily";
import { CountryFlag } from "@/lib/identity";
import { Plus, PencilSimple, Trash, X, MegaphoneSimple } from "@phosphor-icons/react";

const ROLES = [
  { v: "captain", l: "Founding Captain" },
  { v: "moderator", l: "Moderator" },
  { v: "promoter", l: "Promoter" },
];

const blank = () => ({
  name: "", country_code: "NG", role: "captain", contact: "", notes: "", active: true,
});

export default function Ambassadors() {
  const [rows, setRows] = useState([]);
  const [countries, setCountries] = useState([]);
  const [editing, setEditing] = useState(null);  // null | "new" | doc

  const load = () => api.get("/admin/ambassadors")
    .then((r) => setRows(r.data || []))
    .catch(() => setRows([]));

  useEffect(() => {
    load();
    api.get("/countries").then(({ data }) => setCountries(data?.countries || [])).catch(() => {});
  }, []);

  const save = async (doc) => {
    try {
      if (doc.ambassador_id) {
        await api.patch(`/admin/ambassadors/${doc.ambassador_id}`, doc);
        toast.success("Ambassador updated");
      } else {
        await api.post("/admin/ambassadors", doc);
        toast.success("Ambassador added");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const remove = async (amb_id) => {
    if (!confirm("Delete this ambassador?")) return;
    try {
      await api.delete(`/admin/ambassadors/${amb_id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const grouped = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      m[r.country_code] = m[r.country_code] || [];
      m[r.country_code].push(r);
    });
    return m;
  }, [rows]);

  return (
    <div className="space-y-6">
      <Heading
        title="Community Ambassadors"
        subtitle="Trusted local growth reps and moderators. One Founding Captain per country recommended; add Moderators + Promoters as the community grows."
      />

      <button onClick={() => setEditing("new")}
              data-testid="ambassador-create-btn"
              className="px-4 py-2 bg-nk-gold text-nk-bg text-sm font-medium rounded-sm inline-flex items-center gap-2 hover:bg-nk-goldSoft transition-colors">
        <Plus size={14} weight="bold" /> Add ambassador
      </button>

      {rows.length === 0 ? (
        <div className="nk-card p-10 text-center text-nk-mute text-sm">
          <MegaphoneSimple size={28} weight="duotone" className="text-nk-dim mx-auto mb-2" />
          No ambassadors yet. Add a Founding Captain for an emerging country to kick off local growth.
        </div>
      ) : (
        <div className="space-y-5">
          {Object.entries(grouped).map(([code, items]) => (
            <section key={code} data-testid={`ambassadors-country-${code.toLowerCase()}`}>
              <div className="flex items-center gap-2 mb-3">
                <CountryFlag country={code} size={18} />
                <p className="font-medium">{countries.find((c) => c.country_code === code)?.country_name || code}</p>
                <span className="text-xs text-nk-dim font-mono">{items.length}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {items.map((r) => (
                  <article key={r.ambassador_id} className="nk-card p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">{r.name}</p>
                        <p className="text-[10px] text-nk-mute uppercase tracking-wider mt-0.5">
                          {ROLES.find((x) => x.v === r.role)?.l || r.role}
                          {!r.active && <span className="ml-2 text-nk-warn">Inactive</span>}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => setEditing(r)} className="p-1.5 text-nk-mute hover:text-nk-text">
                          <PencilSimple size={12} />
                        </button>
                        <button onClick={() => remove(r.ambassador_id)} className="p-1.5 text-nk-mute hover:text-nk-bad">
                          <Trash size={12} />
                        </button>
                      </div>
                    </div>
                    {r.contact && <p className="text-xs text-nk-mute mb-1">{r.contact}</p>}
                    {r.notes && <p className="text-xs text-nk-dim line-clamp-2">{r.notes}</p>}
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {editing && (
        <Editor
          doc={editing === "new" ? blank() : { ...editing }}
          countries={countries}
          onCancel={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function Editor({ doc, countries, onCancel, onSave }) {
  const [d, setD] = useState(doc);
  const set = (k, v) => setD({ ...d, [k]: v });
  const submit = (e) => { e.preventDefault(); onSave(d); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onCancel}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            className="nk-card-gold p-5 w-full max-w-md rounded-sm space-y-3"
            data-testid="ambassador-editor">
        <div className="flex items-center justify-between">
          <p className="eyebrow text-nk-gold">{d.ambassador_id ? "Edit ambassador" : "New ambassador"}</p>
          <button type="button" onClick={onCancel} className="text-nk-mute hover:text-nk-text"><X size={14} /></button>
        </div>

        <Field label="Name">
          <input value={d.name} onChange={(e) => set("name", e.target.value)} required
                 data-testid="ambassador-name"
                 className="w-full bg-nk-surface border border-nk-line px-3 py-2 text-sm rounded-sm outline-none focus:border-nk-gold" />
        </Field>

        <Field label="Country">
          <select value={d.country_code} onChange={(e) => set("country_code", e.target.value)}
                  data-testid="ambassador-country"
                  className="w-full bg-nk-surface border border-nk-line px-3 py-2 text-sm rounded-sm outline-none focus:border-nk-gold">
            {countries.map((c) => (
              <option key={c.country_code} value={c.country_code}>{c.country_name}</option>
            ))}
          </select>
        </Field>

        <Field label="Role">
          <div className="grid grid-cols-3 gap-2">
            {ROLES.map((r) => (
              <button type="button" key={r.v} onClick={() => set("role", r.v)}
                      className={`px-2 py-1.5 border rounded-sm text-xs ${d.role === r.v ? "border-nk-gold bg-nk-gold/10 text-nk-gold" : "border-nk-line text-nk-mute hover:border-nk-lineHi"}`}>
                {r.l}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Contact (email, phone, handle)">
          <input value={d.contact} onChange={(e) => set("contact", e.target.value)}
                 placeholder="e.g. ada@example.com or @adaonig"
                 className="w-full bg-nk-surface border border-nk-line px-3 py-2 text-sm rounded-sm outline-none focus:border-nk-gold" />
        </Field>

        <Field label="Notes">
          <textarea value={d.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={3}
                    className="w-full bg-nk-surface border border-nk-line px-3 py-2 text-sm rounded-sm outline-none focus:border-nk-gold" />
        </Field>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!d.active} onChange={(e) => set("active", e.target.checked)} />
          Active
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} className="px-3 py-1.5 text-xs text-nk-mute hover:text-nk-text">Cancel</button>
          <button type="submit"
                  data-testid="ambassador-save"
                  className="px-3 py-1.5 bg-nk-gold text-nk-bg text-xs font-medium rounded-sm">
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <p className="text-[10px] uppercase tracking-wider text-nk-mute mb-1">{label}</p>
      {children}
    </label>
  );
}
